import {
  createWorkerExperienceRequestSchema,
  deleteMeRequestSchema,
  deleteMeResponseSchema,
  emailRequestVerifyRequestSchema,
  emailRequestVerifyResponseSchema,
  emailVerifyRequestSchema,
  emailVerifyResponseSchema,
  patchMeEmployerRequestSchema,
  patchMeWorkerLocationRequestSchema,
  patchMeWorkerRequestSchema,
  postMeAvatarRequestSchema,
  postMeAvatarResponseSchema,
  userMeSchema,
  workerDashboardStatsSchema,
  workerExperienceSchema,
  type CreateWorkerExperienceRequest,
  type DeleteMeRequest,
  type EmailRequestVerifyRequest,
  type EmailVerifyRequest,
  type PatchMeEmployerRequest,
  type PatchMeWorkerLocationRequest,
  type PatchMeWorkerRequest,
  type PostMeAvatarRequest,
} from "@chotca/config";
import { z } from "zod";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export function createUsersResource(ctx: RequestContext) {
  return {
    me() {
      return requestJson(ctx, {
        method: "GET",
        path: "/me",
        dataSchema: userMeSchema,
        auth: true,
      });
    },

    patchWorker(body: PatchMeWorkerRequest) {
      const payload = patchMeWorkerRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: "/me/worker",
        body: payload,
        dataSchema: userMeSchema,
        auth: true,
      });
    },

    patchEmployer(body: PatchMeEmployerRequest) {
      const payload = patchMeEmployerRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: "/me/employer",
        body: payload,
        dataSchema: userMeSchema,
        auth: true,
      });
    },

    requestAvatarUpload(body: PostMeAvatarRequest) {
      const payload = postMeAvatarRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/me/avatar",
        body: payload,
        dataSchema: postMeAvatarResponseSchema,
        auth: true,
      });
    },

    requestEmailVerify(body: EmailRequestVerifyRequest) {
      const payload = emailRequestVerifyRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/me/email/request-verify",
        body: payload,
        dataSchema: emailRequestVerifyResponseSchema,
        auth: true,
      });
    },

    verifyEmail(body: EmailVerifyRequest) {
      const payload = emailVerifyRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/me/email/verify",
        body: payload,
        dataSchema: emailVerifyResponseSchema,
        auth: true,
      });
    },

    /** GPS update — debounced server side. */
    patchLocation(body: PatchMeWorkerLocationRequest) {
      const payload = patchMeWorkerLocationRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: "/me/worker/location",
        body: payload,
        dataSchema: z.object({ skipped: z.boolean() }),
        auth: true,
      });
    },

    dashboardStats() {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/worker/dashboard-stats",
        dataSchema: workerDashboardStatsSchema,
        auth: true,
      });
    },

    listExperience() {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/worker/experience",
        dataSchema: z.array(workerExperienceSchema),
        auth: true,
      });
    },

    addExperience(body: CreateWorkerExperienceRequest) {
      const payload = createWorkerExperienceRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/me/worker/experience",
        body: payload,
        dataSchema: workerExperienceSchema,
        auth: true,
      });
    },

    updateExperience(expId: string, body: CreateWorkerExperienceRequest) {
      const payload = createWorkerExperienceRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/me/worker/experience/${expId}`,
        body: payload,
        dataSchema: workerExperienceSchema,
        auth: true,
      });
    },

    deleteMe(body: DeleteMeRequest) {
      const payload = deleteMeRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "DELETE",
        path: "/me",
        body: payload,
        dataSchema: deleteMeResponseSchema,
        auth: true,
      });
    },

    deleteExperience(expId: string) {
      return requestJson(ctx, {
        method: "DELETE",
        path: `/me/worker/experience/${expId}`,
        dataSchema: z.object({ ok: z.literal(true) }),
        auth: true,
      });
    },
  };
}
